import { RECEIPT_STATUS_LABELS, type ReceiptItem, type ReceiptStatus, type UploadSlots, looselyIncludes } from "@nmt/shared";
import { PaidBadge } from "../components/paid-badge";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { errorMessage, get, post, put } from "../api";
import { Badge, Button, Empty, Loading, PageHeader, Panel, PanelHeader, Tabs, formatCents, formatDate } from "../components/ui";
import { isImage, photoInfo } from "../lib/images";
import { useSession } from "../session";
import { useRememberedTab } from "../components/use-tab";
import { ExpenseSyncDialog } from "../components/qb-expense-sync";

const TONE: Record<ReceiptStatus, "ok" | "wait" | "off" | "brand"> = { reading: "wait", read_failed: "brand", draft: "wait", ready: "brand", approved: "ok" };

/** Uploads a receipt photo or PDF to the job's files (or the company's, for overhead) and turns it in. */
async function turnIn(jobId: string | null, f: File): Promise<ReceiptItem> {
  const contentType = f.type || "application/octet-stream";
  const photo = isImage(contentType, f.name);
  const base = jobId ? `/api/jobs/${jobId}/files` : "/api/company/files";
  const [slot] = await post<UploadSlots[]>(`${base}/upload-urls`, { files: [{ name: f.name, contentType, size: f.size, variants: photo }] });
  const put = async (s: { url: string; headers: Record<string, string> }, body: Blob) => {
    const res = await fetch(s.url, { method: "PUT", headers: s.headers, body });
    if (!res.ok) throw new Error("Upload failed. Try again.");
  };
  const info = photo ? await photoInfo(f) : null;
  await put(slot!.original, f);
  // The medium copy is what gets read when the original is a phone's HEIC.
  if (info?.thumb && slot!.thumb) await put(slot!.thumb, info.thumb).catch(() => undefined);
  if (info?.medium && slot!.medium) await put(slot!.medium, info.medium).catch(() => undefined);
  const saved = await post<{ id: string }>(base, {
    key: slot!.original.key,
    thumbKey: slot!.thumb?.key ?? null,
    mediumKey: slot!.medium?.key ?? null,
    name: f.name,
    contentType,
    size: f.size,
    isPhoto: photo,
    category: "Invoices and receipts",
    width: info?.width ?? null,
    height: info?.height ?? null,
    takenAt: info?.takenAt ?? null,
  });
  return post<ReceiptItem>("/api/receipts", { fileId: saved.id });
}

function TurnInButton({ jobId, onDone, label = "Add receipt or bill" }: { jobId: string | null; onDone: (r: ReceiptItem) => void; label?: string }) {
  const input = useRef<HTMLInputElement>(null);
  const go = useMutation({ mutationFn: (f: File) => turnIn(jobId, f), onSuccess: onDone });
  return (
    <>
      <input
        ref={input}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) go.mutate(f);
          e.target.value = "";
        }}
      />
      <Button variant="primary" className="px-3 py-1.5" busy={go.isPending} onClick={() => input.current?.click()}>
        {label}
      </Button>
      {go.error && <span className="text-sm text-danger">{errorMessage(go.error)}</span>}
    </>
  );
}

/** On a job's list: that job's share of a receipt (its lines there; a bill can cover several jobs). Elsewhere: the whole. */
function amountOn(r: ReceiptItem, jobId?: string): number | null {
  if (jobId && r.lines.some((l) => l.jobId)) return r.lines.filter((l) => l.jobId === jobId).reduce((t, l) => t + l.amountCents, 0);
  return r.totalCents !== null ? (r.kind === "credit" ? -r.totalCents : r.totalCents) : null;
}

function Rows({ rows, showJob, jobId, onOpen }: { rows: ReceiptItem[]; showJob?: boolean; jobId?: string; onOpen: (r: ReceiptItem) => void }) {
  const lines = (r: ReceiptItem) => (jobId && r.lines.some((l) => l.jobId) ? r.lines.filter((l) => l.jobId === jobId) : r.lines);
  const amount = (r: ReceiptItem) => {
    const a = amountOn(r, jobId);
    return a !== null ? formatCents(a) : "—";
  };
  return (
    <ul>
      {rows.map((r) =>
        r.voided ? (
          // Deleted in QuickBooks: shown for the record, counted nowhere, can't be opened or changed.
          <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-mist px-5 py-3 text-muted last:border-0" title={r.voided.reason}>
            <Badge tone="off">Void</Badge>
            <span className="font-semibold line-through">{r.vendorName || r.file?.name || "Receipt"}</span>
            <span className="min-w-0 flex-1 truncate text-sm">{[r.voided.reason, formatDate(r.voided.at.slice(0, 10)), r.date ? `dated ${formatDate(r.date)}` : null].filter(Boolean).join(" · ")}</span>
            <span className="line-through">{amount(r)}</span>
          </li>
        ) : (
          <li key={r.id} className="border-b border-mist last:border-0">
            <button onClick={() => onOpen(r)} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-5 py-3 text-left hover:bg-mist/50">
            <Badge tone={TONE[r.status]}>{RECEIPT_STATUS_LABELS[r.status]}</Badge>
            <span className="font-semibold">{r.vendorName || r.file?.name || "Receipt"}</span>
            {r.source === "quickbooks" && <Badge tone="off">From QuickBooks</Badge>}
            <span className="min-w-0 flex-1 truncate text-sm text-muted">
              {[r.kind === "bill" ? "Bill" : r.kind === "credit" ? "Credit" : "Expense", lines(r).length > 1 ? `${lines(r).length} lines` : (lines(r)[0]?.category ?? r.category), r.date ? formatDate(r.date) : null, showJob ? r.jobLabel : null, r.submittedBy?.name].filter(Boolean).join(" · ")}
            </span>
            {r.warnings.length > 0 && <Badge tone="brand">Check</Badge>}
            {r.kind === "bill" && r.takeDiscount && r.earlyDiscountBy && r.earlyDiscountCents !== null && !r.qbPaidAt && !r.discountLapsedAt && <Badge tone="wait">Pay by {formatDate(r.earlyDiscountBy)} for {formatCents(r.earlyDiscountCents)} off</Badge>}
            <PaidBadge r={r} />
            <span className="font-semibold">{amount(r)}</span>
            </button>
          </li>
        ),
      )}
    </ul>
  );
}

/** The job's Expenses tab. */
export function JobReceipts({ jobId }: { jobId: string }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["receipts", "job", jobId], queryFn: () => get<ReceiptItem[]>(`/api/jobs/${jobId}/receipts`), refetchInterval: (q) => (q.state.data?.some((r) => r.status === "reading") ? 2000 : false) });
  const navigate = useNavigate();
  const setOpen = (id: string) => navigate(`/receipts/${id}`);
  const [syncing, setSyncing] = useState(false);
  // This job's share: its lines (a bill can cover several jobs); a credit's lines are negative.
  const live = list.data?.filter((r) => !r.voided) ?? [];
  const voided = (list.data?.length ?? 0) - live.length;
  const total = live.reduce((s, r) => s + (amountOn(r, jobId) ?? 0), 0);
  return (
    <Panel>
      <PanelHeader
        title="Receipts and bills"
        description={list.data?.length ? `${live.length} on this job, ${formatCents(total)} in all${voided ? ` (and ${voided} void: deleted in QuickBooks)` : ""}.` : "Take a photo of a receipt or upload a supplier's bill; it's read for you."}
        action={
          <div className="flex flex-wrap items-center gap-2">
            {can("receipts.approve") && (
              <Button className="px-3 py-1.5" onClick={() => setSyncing(true)}>
                Refresh from QuickBooks
              </Button>
            )}
            {can("receipts.submit", "receipts.approve") && (
              <TurnInButton
                jobId={jobId}
                onDone={async (r) => {
                  await qc.invalidateQueries({ queryKey: ["receipts"] });
                  setOpen(r.id);
                }}
              />
            )}
          </div>
        }
      />
      {syncing && <ExpenseSyncDialog jobId={jobId} onClose={() => setSyncing(false)} />}
      {list.isLoading ? <div className="px-5"><Loading /></div> : !list.data?.length ? <Empty>No receipts yet.</Empty> : <Rows rows={list.data} jobId={jobId} onOpen={(r) => setOpen(r.id)} />}
    </Panel>
  );
}

/** The review queue. */
export function ReceiptsPage() {
  const { can } = useSession();
  const [tab, setTab] = useRememberedTab<"review" | "ready" | "approved" | "general" | "all" | "settings">("receipts", can("receipts.approve") ? ["review", "ready", "approved", "general", "all", "settings"] : ["review", "ready", "approved", "general", "all"], can("receipts.approve") ? "ready" : "review");
  const navigate = useNavigate();
  const setOpen = (id: string) => navigate(`/receipts/${id}`);
  const list = useQuery({ queryKey: ["receipts", tab], queryFn: () => get<ReceiptItem[]>(`/api/receipts?status=${tab}`), enabled: tab !== "settings", refetchInterval: (q) => (q.state.data?.some((r) => r.status === "reading") ? 2000 : 60_000) });
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Receipts and bills"
        description="Add job receipts from the job's Expenses tab, and overhead (no job: office, tools, fuel) here. Check what was read, mark it Ready, and it's approved here."
        action={can("receipts.submit") && can("files.upload") && <TurnInButton jobId={null} label="Add overhead receipt" onDone={(r) => setOpen(r.id)} />}
      />
      <Tabs
        tabs={[
          { value: "review", label: "Needs review" },
          { value: "ready", label: "Ready to approve" },
          { value: "approved", label: "Approved" },
          { value: "general", label: "General expenses" },
          { value: "all", label: "All" },
          ...(can("receipts.approve") ? [{ value: "settings" as const, label: "Settings" }] : []),
        ]}
        value={tab}
        onChange={setTab}
      />
      {tab === "settings" ? (
        <TrustedVendors />
      ) : (
      <Panel>{list.isLoading ? <div className="px-5"><Loading /></div> : !list.data?.length ? <Empty>Nothing here.</Empty> : <Rows rows={list.data} showJob onOpen={(r) => setOpen(r.id)} />}</Panel>
      )}
    </div>
  );
}


type TrustedVendor = { id: string; name: string; category: string; trusted: boolean; maxCents: number | null; receipts: number };

/** Receipts → Settings: vendors whose receipts are approved without review (none by default). */
function TrustedVendors() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const list = useQuery({ queryKey: ["trusted-vendors"], queryFn: () => get<TrustedVendor[]>("/api/receipts/trusted-vendors") });
  const save = useMutation({
    mutationFn: (v: TrustedVendor) => put(`/api/receipts/trusted-vendors/${v.id}`, { trusted: v.trusted, maxCents: v.maxCents }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["trusted-vendors"] }),
  });
  const shown = (list.data ?? []).filter((v) => looselyIncludes(v.name, q));
  return (
    <Panel>
      <PanelHeader
        title="Trusted vendors"
        description="A trusted vendor's receipt or bill is approved and sent to QuickBooks without review when it was read cleanly, is on one clear job, has nothing missing, and is at or under the limit. A sub with paperwork problems, and anything unclear, still waits here. No vendor is trusted unless you tick it."
      />
      <div className="px-5 py-3">
        <input className="w-full max-w-sm rounded-[var(--radius-control)] border border-line px-3 py-2 text-base sm:text-[15px]" placeholder="Find a vendor or sub…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {list.isLoading ? (
        <div className="px-5"><Loading /></div>
      ) : !shown.length ? (
        <Empty>No vendors or subs.</Empty>
      ) : (
        <ul className="max-h-[36rem] overflow-y-auto">
          {shown.map((v) => (
            <TrustedRow key={v.id} v={v} busy={save.isPending && save.variables?.id === v.id} onSave={(next) => save.mutate(next)} />
          ))}
        </ul>
      )}
    </Panel>
  );
}

function TrustedRow({ v, busy, onSave }: { v: TrustedVendor; busy: boolean; onSave: (v: TrustedVendor) => void }) {
  const [limit, setLimit] = useState(v.maxCents === null ? "" : String(v.maxCents / 100));
  const cents = limit.trim() === "" ? null : Math.round(Number(limit) * 100);
  const bad = cents !== null && !(cents >= 0);
  return (
    <li className="flex flex-wrap items-center gap-3 border-b border-mist px-5 py-2.5 text-sm last:border-0">
      <label className="flex min-w-0 flex-1 items-center gap-2">
        <input type="checkbox" className="size-4 accent-[var(--color-brand)]" checked={v.trusted} disabled={busy} onChange={(e) => onSave({ ...v, trusted: e.target.checked, maxCents: bad ? null : cents })} />
        <span className="truncate font-medium">{v.name}</span>
        <span className="text-xs text-muted">{v.category === "subcontractor" ? "Sub" : "Vendor"} · {v.receipts} receipt{v.receipts === 1 ? "" : "s"}</span>
      </label>
      {v.trusted && (
        <span className="flex items-center gap-1.5">
          <span className="text-xs text-muted">Up to $</span>
          <input className="w-24 rounded-[var(--radius-control)] border border-line px-2 py-1 text-base sm:text-sm" inputMode="decimal" placeholder="Any" value={limit} onChange={(e) => setLimit(e.target.value)} />
          <Button className="px-2.5 py-1" disabled={busy || bad || cents === v.maxCents} onClick={() => onSave({ ...v, maxCents: cents })}>
            Save
          </Button>
        </span>
      )}
    </li>
  );
}
